import React, { useState } from 'react';
import { useIntl } from 'umi';
import BasicImage from '@/pages/home/components/BasicImage';
import { useReportToolbarActions } from './hooks/useReportToolbarActions';
import { useReportToolbarDisplay } from './hooks/useReportToolbarDisplay';
import HtmlPublishModal from './HtmlPublishModal';
import aiEditIcon from '@/pages/home/assets/svg/exploreHeader/icon-ai-edit.svg';
import downloadIcon from '@/pages/home/assets/svg/exploreHeader/icon-download.svg';
import publishIcon from '@/pages/home/assets/svg/exploreHeader/icon-publish.svg';
import closeIcon from '@/pages/home/assets/svg/exploreHeader/icon-toolbar-close.svg';
import './index.scss';

/**
 * AI Gallery Report 顶部工具条组件
 *
 * 业务组件（Layer 1 + Layer 2）：
 * 渲染标题（i18n 中英文）与「AI编辑」「下载」「发布」操作按钮；
 * 业务行为（拉起 Copilot、下载报告、下载提示）由内部 hook useReportToolbarActions 提供；
 * 与 iframe 内容页的联动展示（背景色跟随、滚动显隐）由 useReportToolbarDisplay 提供。
 * 详情接口非成功（含 40301 无权限）时不渲染，避免无权限页仍露出工具条。
 */
interface IReportToolbarProps {
  /** 报告在 COS 的路径（/as/report/...） */
  path: string;
  /** 报告页面名（URL query name，作为 Copilot 引用来源的文件标签） */
  pageName: string;
  /** 报告内容 iframe 的引用，用于读取背景色与监听内部滚动 */
  iframeRef: React.RefObject<HTMLIFrameElement>;
}

/** 工具条按钮类型，作为行为映射（actionMap）的 key */
type TReportToolbarButtonType = 'aiEdit' | 'download' | 'publish';

/** 工具条按钮配置项 */
interface IReportToolbarButtonConfig {
  /** 按钮类型，对应行为映射中的处理函数 */
  type: TReportToolbarButtonType;
  /** i18n 文案 key */
  labelKey: string;
  /** 按钮图标资源 */
  icon: string;
  /** 是否显示（与权限等动态条件叠加） */
  visible: boolean;
}

/**
 * 工具条按钮配置 map（数组保证渲染顺序：Edit → Download → Publish）
 * 新增按钮时：在此追加配置项，并在 actionMap 中补充对应处理函数即可
 */
const REPORT_TOOLBAR_BUTTON_CONFIG: IReportToolbarButtonConfig[] = [
  {
    type: 'aiEdit',
    labelKey: 'dbh_studio_aigallery_report_edit',
    icon: aiEditIcon,
    visible: true,
  },
  {
    type: 'download',
    labelKey: 'dbh_studio_aigallery_report_download',
    icon: downloadIcon,
    visible: true,
  },
  {
    type: 'publish',
    labelKey: 'dbh_studio_aigallery_report_publish',
    icon: publishIcon,
    visible: true,
  },
];

/**
 * 报告顶部工具条组件
 * @param props - 组件属性
 * @param props.path - 报告在 COS 的路径
 * @param props.pageName - 报告页面名，作为 Copilot 引用来源的文件标签
 * @param props.iframeRef - 报告内容 iframe 的引用，用于读取背景色与监听内部滚动
 */
const ReportToolbar: React.FC<IReportToolbarProps> = ({
  path,
  pageName,
  iframeRef,
}) => {
  const { formatMessage } = useIntl();
  // 标题直接使用外部传入的页面名，无默认文案
  const toolbarTitle = pageName;
  const {
    canShowToolbar,
    showEdit,
    showPublish,
    isDownloading,
    handleEdit,
    handleDownload,
  } = useReportToolbarActions({ path, title: toolbarTitle, pageName });
  const [publishVisible, setPublishVisible] = useState(false);
  const { bgColor, scrollVisible, isDismissed, dismissToolbar } =
    useReportToolbarDisplay(iframeRef, path);

  const showToolbar = canShowToolbar && !isDismissed;
  // 工具栏背景色通过 CSS 自定义属性透传（须与 index.scss 的 --dbh-report-toolbar-bg 一致）
  const toolbarStyle: React.CSSProperties & Record<'--dbh-report-toolbar-bg', string> = {
    '--dbh-report-toolbar-bg': bgColor,
  };

  /**
   * 根据按钮类型执行对应的点击行为（事件内直接映射）
   * @param type - 按钮类型
   */
  const handleButtonClick = (type: TReportToolbarButtonType): void => {
    const actionMap: Record<TReportToolbarButtonType, () => void> = {
      aiEdit: handleEdit,
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      download: handleDownload,
      publish: () => setPublishVisible(true),
    };
    actionMap[type]?.();
  };

  if (!showToolbar) {
    return null;
  }

  return (
    <>
      <div
        className={`dbh-aigallery-report-toolbar${
          scrollVisible ? '' : ' dbh-aigallery-report-toolbar--hidden'
        }`}
        style={toolbarStyle}
      >
        <div className="dbh-aigallery-report-toolbar__title" title={toolbarTitle}>
          {toolbarTitle}
        </div>
        <div className="dbh-aigallery-report-toolbar__actions">
          {REPORT_TOOLBAR_BUTTON_CONFIG.filter(
            (btn) =>
              btn.visible &&
              (btn.type !== 'aiEdit' || showEdit) &&
              (btn.type !== 'publish' || showPublish),
          ).map((btn) => (
            <button
              key={btn.type}
              type="button"
              className="dbh-aigallery-report-toolbar__btn"
              onClick={() => handleButtonClick(btn.type)}
              disabled={isDownloading && btn.type === 'download'}
            >
              <BasicImage
                src={btn.icon}
                alt={formatMessage({ id: btn.labelKey })}
                className="dbh-aigallery-report-toolbar__icon"
              />
              <span>{formatMessage({ id: btn.labelKey })}</span>
            </button>
          ))}
          <button
            type="button"
            className="dbh-aigallery-report-toolbar__close"
            onClick={dismissToolbar}
            aria-label={formatMessage({
              id: 'dbh_studio_aigallery_report_toolbar_close',
            })}
          >
            <BasicImage
              src={closeIcon}
              alt=""
              className="dbh-aigallery-report-toolbar__close-icon"
            />
          </button>
        </div>
      </div>
      {publishVisible && showPublish && (
        <HtmlPublishModal
          htmlPath={path}
          onClose={() => setPublishVisible(false)}
        />
      )}
    </>
  );
};

export default ReportToolbar;
